import React from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'

type MyButtonVariant =
  | 'redpink-anthracite'
  | 'anthracite-redpink'
  | 'anthracite'
  | 'lightgray-redpink'
  | 'lightgray'
  | 'redpink'
  | 'redpink-inactive'
  | 'default'
  | 'whatsapp'
  | 'green'
  | 'green-gray'
  | 'darkgray'
  | 'close-modal'
  | 'blue'

interface MyButtonProps {
  variant?: MyButtonVariant
  className?: string
  children?: React.ReactNode
  to?: string
  tooltipContent?: string
  tooltipId?: string
  showTooltip?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  iconClassName?: string
  width?: string
  [key: string]: any
}

const getClasses = (variant: MyButtonVariant, isDisabled: boolean) => {
  switch (variant) {
    case 'redpink-anthracite':
      return 'w-full sm:w-auto bg-redpink text-white hover:bg-redpinkDark hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-lg'
    case 'anthracite-redpink':
      return 'w-full sm:w-full bg-anthracite text-white rounded-lg hover:bg-anthracite/90 px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base hover:text-white inline-block'
    case 'anthracite':
      return 'w-full sm:w-auto bg-anthracite text-white hover:bg-anthraciteLight px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base rounded-md hover:text-white'
    case 'lightgray-redpink':
      return 'w-full sm:w-auto bg-grisclair text-anthracite hover:bg-redpink hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base'
    case 'lightgray':
      return 'w-full sm:w-auto bg-grisclair text-anthracite hover:bg-grisclair hover:text-anthracite px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base'
    case 'darkgray':
      return 'w-full sm:w-auto bg-grisfonce2 text-white rounded-md hover:bg-grisfonce px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base'
    case 'redpink':
      return 'w-full sm:w-auto bg-redpink text-white hover:bg-redpinkDark hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-md'
    case 'redpink-inactive':
      return 'w-full sm:w-auto bg-redpink opacity-50 text-white hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-md'
    case 'whatsapp':
      return 'w-full sm:w-auto bg-whatsapp hover:bg-whatsappDark text-white hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-md'
    case 'green':
      return 'w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-md'

    case 'green-gray':
      return clsx(
        'w-full sm:w-auto  px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-md',
        {
          'bg-emerald-500 hover:bg-emerald-600 text-white': !isDisabled,
          'bg-grisclair text-anthracite/50': isDisabled,
        },
      )
    case 'blue':
      return 'w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base inline-block rounded-md'
    case 'close-modal':
      return 'bg-redpink text-white absolute -right-2 sm:-right-3 -top-2 sm:-top-3 shadow-md border-2 border-white p-0.5 sm:p-1 rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center hover:bg-anthracite hover:text-white'
    default:
      return 'w-full sm:w-auto bg-lightgray text-anthracite hover:bg-redpink hover:text-white px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base'
  }
}

const MyButton: React.FC<MyButtonProps> = ({
  variant = 'default',
  children,
  className,
  to,
  tooltipContent,
  tooltipId,
  icon,
  iconPosition = 'left',
  iconClassName,
  showTooltip = false,
  disabled = false,
  ...props
}) => {
  const buttonClasses = clsx(
    getClasses(variant, disabled),
    'transition-all duration-300',
    className,
  )

  const iconClasses = clsx(
    'flex items-center justify-center mx-auto gap-2',
    iconClassName,
  )
  const buttonContent = (
    <span className={iconClasses}>
      {icon && iconPosition === 'left' && icon}
      {children}
      {icon && iconPosition === 'right' && icon}
    </span>
  )

  const button = (
    <button
      className={buttonClasses}
      data-tip={tooltipContent}
      data-for={tooltipId}
      {...props}
    >
      {buttonContent}
    </button>
  )

  return (
    <>
      {to ? (
        <Link
          to={to}
          className={buttonClasses}
          data-tip={tooltipContent}
          data-for={tooltipId}
          {...props}
        >
          {buttonContent}
        </Link>
      ) : showTooltip ? (
        <Tippy content={tooltipContent || ''}>{button}</Tippy>
      ) : (
        button
      )}
    </>
  )
}

export default MyButton
